import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Chip, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import BugReportIcon from '@mui/icons-material/BugReport';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useNavigate } from 'react-router-dom';
import {
  getThreatSummary, triggerGDRefresh, getGDRefreshJob, getGDActiveRefreshJob,
} from '../../../services/guarddutyApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalErrorState, PortalSectionCard,
} from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants,
} from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, statTileSx, statTileFooterSx, tableHeadRowSx,
  headCellSx, rowSx, bodyCellSx, statusChipSx, emptyTextSx, tileEntranceStyle
} from '../portalStyles';
import { severityColor } from './severity';

const SEVERITY_ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// GuardDuty accent, shared with every other GuardDuty route.
const GD = '#E65100';
const GD_DARK = '#BF360C';

const refreshBtnSx = {
  backgroundColor: GD,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  '&:hover': { backgroundColor: GD_DARK },
};

// Severity colours are centralized in ./severity; this map only carries the icon.
const severityMeta = {
  CRITICAL: { icon: <ErrorOutlineIcon sx={{ fontSize: 28 }} /> },
  HIGH:     { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  MEDIUM:   { icon: <WarningAmberIcon sx={{ fontSize: 28 }} /> },
  LOW:      { icon: <InfoOutlinedIcon sx={{ fontSize: 28 }} /> },
};

// relativeTime returns a short "2 min ago" style string for a past ISO time.
const relativeTime = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} h ago`;
  return `${Math.round(diffSec / 86400)} d ago`;
};

export default function ThreatDashboard({ config }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshJob, setRefreshJob] = useState(null);
  const pollRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getThreatSummary();
      setSummary(res?.data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // On mount, discover a refresh that is already running server-side (started
  // before a navigation away or a full page reload) and re-attach the progress
  // bar to it — the poll effect below then takes over exactly as if the user
  // had just clicked Refresh. A failed lookup is ignored (never treated as
  // "no job"), and a refresh the user already started this session wins.
  useEffect(() => {
    let ignore = false;
    getGDActiveRefreshJob()
      .then((res) => {
        const job = res?.data;
        if (!ignore && job && job.status === 'running') {
          setRefreshJob((prev) => prev || job);
        }
      })
      .catch(() => { /* transient — the user can still refresh manually */ });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    if (!refreshJob || refreshJob.status !== 'running') return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const res = await getGDRefreshJob(refreshJob.id);
        const next = res?.data;
        setRefreshJob(next);
        if (next && next.status !== 'running') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          await loadSummary();
        }
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [refreshJob, loadSummary]);

  const onRefresh = async () => {
    try {
      const res = await triggerGDRefresh();
      setRefreshJob(res?.data);
    } catch (e) {
      setError(e.message);
    }
  };

  // The deployment line is always known (config comes from the parent route), so
  // the header can stay mounted through loading and error states.
  const deploymentLine = (
    <>
      Admin account <b>{config.adminAccountId}</b> • Home region <b>{config.homeRegion}</b>
    </>
  );

  if (loading && !summary) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<GppMaybeIcon sx={{ fontSize: 24 }} />}
            title="Threat Dashboard"
            color={GD}
            subtitle={deploymentLine}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading threat summary…" color={GD} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader
            icon={<GppMaybeIcon sx={{ fontSize: 24 }} />}
            title="Threat Dashboard"
            color={GD}
            subtitle={deploymentLine}
          />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => loadSummary()} />
        </motion.div>
      </motion.div>
    );
  }

  if (!summary) return null;

  const totalThreats = SEVERITY_ORDER.reduce(
    (n, s) => n + (summary.severityCounts?.[s] ?? 0),
    0
  );
  const refreshing = refreshJob?.status === 'running';
  const lastRefreshText = summary.lastRefreshAt
    ? relativeTime(summary.lastRefreshAt)
    : 'never';

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* === Header === */}
      <motion.div variants={sectionVariants}>
      <PortalPageHeader
        icon={<GppMaybeIcon sx={{ fontSize: 24 }} />}
        title="Threat Dashboard"
        color={GD}
        subtitle={(
          <>
            {deploymentLine}
            {' • '}Last refresh: <b>{lastRefreshText}</b>
            {refreshJob?.status === 'completed' && (
              <> • <span>{refreshJob.findingsFetched} findings fetched</span></>
            )}
          </>
        )}
        action={(
          <Tooltip title={refreshing ? 'Refresh already in progress' : 'Fetch the latest findings from AWS'}>
            <span>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                disabled={refreshing}
                onClick={onRefresh}
                sx={refreshBtnSx}
              >
                {refreshing ? 'Refreshing…' : 'Refresh findings'}
              </Button>
            </span>
          </Tooltip>
        )}
      />
      </motion.div>

      {refreshing && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={refreshJob?.progress} label={refreshJob?.progressLabel || 'Refreshing findings…'} sx={{ mb: 3 }} />
        </motion.div>
      )}

      {config.status === 'partial_failure' && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={
              <Button
                size="small"
                onClick={() => navigate('/securityhub/guardduty/enrollment')}
                sx={{ fontFamily: FONT_BODY, fontWeight: 600, textTransform: 'none', color: 'inherit' }}
              >
                Review
              </Button>
            }
          >
            GuardDuty setup partially failed. {config.errorMessage}
          </Alert>
        </motion.div>
      )}

      {/* === Severity stat cards ===
          No motion wrapper: the tiles animate themselves in pure CSS, and a
          motion component here can write an inline opacity onto them once its
          own animation settles. */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {SEVERITY_ORDER.map((sev, i) => {
          const n = summary.severityCounts?.[sev] ?? 0;
          const m = severityMeta[sev];
          const color = severityColor(sev);
          return (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={sev}>
              <div style={tileEntranceStyle(i)}>
                <Paper
                  elevation={0}
                  sx={{
                    ...statTileSx,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { borderColor: color, boxShadow: `0 4px 12px ${color}20` },
                  }}
                  onClick={() => navigate(`/securityhub/guardduty/findings?severity=${sev}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: '10px',
                        backgroundColor: `${color}12`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color,
                      }}
                    >
                      {m.icon}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 700,
                        fontSize: '2.5rem',
                        color,
                        lineHeight: 1,
                      }}
                    >
                      {n}
                    </Typography>
                  </Box>
                  <Box sx={statTileFooterSx}>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: CK.navy,
                        mb: 0.5,
                      }}
                    >
                      {sev.charAt(0) + sev.slice(1).toLowerCase()}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontSize: '0.8rem',
                        color: CK.muted,
                        fontWeight: 500,
                      }}
                    >
                      {n === 1 ? 'active threat' : 'active threats'}
                    </Typography>
                  </Box>
                </Paper>
              </div>
            </Grid>
          );
        })}
      </Grid>

      <motion.div variants={sectionVariants}>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted, display: 'block', mb: 3 }}>
          Total active threats across all severities: <b>{totalThreats}</b>
        </Typography>
      </motion.div>

      {/* === Top threat types === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<BugReportIcon sx={{ fontSize: 20 }} />}
          title="Top threat types"
          subtitle="Select a type to open the matching findings"
          color={GD}
          action={(
            <Chip
              size="small"
              label={`${(summary.perType || []).length} types`}
              sx={statusChipSx('info')}
            />
          )}
          sx={{ mb: 3 }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Type</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 120 }}>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.perType || []).map((row, index) => (
                  <TableRow
                    key={row.type}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => navigate('/securityhub/guardduty/findings?type=' + encodeURIComponent(row.type))}
                  >
                    <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{row.type}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        label={row.count}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary.perType || summary.perType.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No threat types detected yet — refresh findings to pull the latest from AWS.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>

      {/* === Per-account table === */}
      <motion.div variants={sectionVariants}>
        <PortalSectionCard
          icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />}
          title="Per-account threats"
          subtitle="Select an account to open the matching findings"
          color={GD}
          action={(
            <Chip
              size="small"
              label={`${(summary.perAccount || []).length} accounts with threats`}
              sx={statusChipSx('info')}
            />
          )}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell align="right" sx={{ ...headCellSx, width: 180 }}>Active threats</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(summary.perAccount || []).map((row, index) => (
                  <TableRow
                    key={row.accountId}
                    hover
                    sx={{ ...rowSx(index), cursor: 'pointer' }}
                    onClick={() => navigate('/securityhub/guardduty/findings?account=' + encodeURIComponent(row.accountId))}
                  >
                    <TableCell sx={bodyCellSx}>{row.accountId}</TableCell>
                    <TableCell align="right" sx={bodyCellSx}>
                      <Chip
                        icon={<WarningAmberIcon />}
                        label={row.count}
                        size="small"
                        sx={{ ...statusChipSx(row.count > 0 ? 'warning' : 'neutral'), minWidth: 80 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {(!summary.perAccount || summary.perAccount.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No accounts with active threats — every enrolled account is clear right now.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </PortalSectionCard>
      </motion.div>
    </motion.div>
  );
}
